import React, { useEffect, useState } from 'react';
import { ChevronLeftIcon } from '@heroicons/react/16/solid';
import { Link, useParams } from 'react-router-dom';
import { Heading, Subheading } from '../../../../components/Heading';
import { Button } from '../../../../components/Button';
import { DescriptionDetails, DescriptionList, DescriptionTerm } from '../../../../components/Description-List';
import { Card } from '../../../../components/Card';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../../../../components/Table';
import Loading from '../../../../components/Loading';
import dateFormat from 'dateformat';
import showErrorToast from '../../../../components/Toasts/ShowErrorToast';
import { useTrackingOrder } from '../../../../hooks/useTrackingOrder';
import { TrackingOrder } from '../../../../models/TrackingOrder';
import QRCode from 'qrcode';
import TrackingOrderService from '../../../../services/trackingOrderService';
import { BlobProvider } from '@react-pdf/renderer';
import PrintOrder from '../PrintOrder/Page';

const ViewTrackingOrderPage: React.FC = () => {
    const { id } = useParams<{ id: string }>();
    const [order, setOrder] = useState<TrackingOrder | null>(null);
    const { fetchTrackingOrderById, loading, error } = useTrackingOrder();
    const [dmQrImage, setDmQrImage] = useState<string | null>(null);
    const [subDmQrImage, setSubDmQrImage] = useState<string | null>(null);
    const [custQrImage, setCustQrImage] = useState<string | null>(null);
    const [dmImages, setDmImages] = useState<{ proven_image?: string | null } | null>(null);
    const [subDmImages, setSubDmImages] = useState<{ proven_image?: string | null } | null>(null);
    const [custImages, setCustImages] = useState<{ proven_image?: string | null; signature_image?: string | null } | null>(null);

    useEffect(() => {
        document.title = "View Tracking Order";
    }, []);

    useEffect(() => {
        const fetchDetails = async () => {
            if (!id) return;
            try {
                const o = await fetchTrackingOrderById(Number(id));
                setOrder(o);
                if (o?.delivery_man_token) {
                    const url = `${window.location.origin}/gp/tracking/standalone/delivery-man/${o.delivery_man_token}`;
                    const img = await QRCode.toDataURL(url, { width: 100 });
                    setDmQrImage(img);
                }
                if (o?.sub_delivery_man_token) {
                    const url = `${window.location.origin}/gp/tracking/standalone/sub-delivery-man/${o.sub_delivery_man_token}`;
                    const img = await QRCode.toDataURL(url, { width: 100 });
                    setSubDmQrImage(img);
                }
                if (o?.customer_token) {
                    const url = `${window.location.origin}/gp/tracking/standalone/customer/${o.customer_token}`;
                    const img = await QRCode.toDataURL(url, { width: 100 });
                    setCustQrImage(img);
                }
                const dmImg = await TrackingOrderService.getDeliveryManImages(Number(id));
                setDmImages(dmImg);
                const subDmImg = await TrackingOrderService.getSubDeliveryManImages(Number(id));
                setSubDmImages(subDmImg);
                const custImg = await TrackingOrderService.getCustomerImages(Number(id));
                setCustImages(custImg);
            } catch (err) {
                showErrorToast('Failed to fetch tracking order details.');
            }
        };
        fetchDetails();
    }, [id]);

    if (loading) return <Loading />;
    if (error) return <p className="py-16 text-center text-red-700">{error}</p>;
    if (!order) return <p className="py-16 text-center">Tracking order not found.</p>;

    const deliveryMan = (order as any).delivery_man;
    const subDeliveryMan = (order as any).sub_delivery_man;
    const customerConfirm = (order as any).customer_confirmation;

    return (
        <>
            <div className="max-lg:hidden">
                <Link to="/gp/tracking/standalone" className="inline-flex items-center gap-2 text-sm/6 text-zinc-500 dark:text-zinc-400">
                    <ChevronLeftIcon className="size-4 fill-zinc-400 dark:fill-zinc-500" />
                    Back to Tracking Orders
                </Link>
            </div>
            <div className="mt-4 lg:mt-8">
                <div className="flex flex-wrap items-center justify-between gap-4">
                    <Heading>{order.reference_no}</Heading>
                    {dmQrImage && custQrImage && (
                        <BlobProvider document={<PrintOrder order={order} dmQrImage={dmQrImage} subDmQrImage={subDmQrImage} custQrImage={custQrImage} />}>
                            {({ url }) => (
                                <a href={url!} target="_blank" rel="noopener noreferrer">
                                    <Button>Print Order</Button>
                                </a>
                            )}
                        </BlobProvider>
                    )}
                </div>
                <div className="mt-2.5 flex flex-wrap gap-x-10 gap-y-4">
                    <span className="text-sm text-zinc-500">
                        {order.document_date
                            ? `DO date ${dateFormat(order.document_date, "mmmm dd, yyyy")}`
                            : `Created on ${dateFormat(order.created_time, "mmmm dd, yyyy")}`}
                    </span>
                </div>
            </div>

            <Card className="mt-12 p-6">
                <Subheading>Order Information</Subheading>
                <DescriptionList className="mt-4">
                    <DescriptionTerm>Reference No</DescriptionTerm>
                    <DescriptionDetails>{order.reference_no}</DescriptionDetails>
                    <DescriptionTerm>Delivery Number</DescriptionTerm>
                    <DescriptionDetails>{order.delivery_number || '-'}</DescriptionDetails>
                    <DescriptionTerm>DO Date</DescriptionTerm>
                    <DescriptionDetails>{order.document_date ? dateFormat(order.document_date, "mmmm dd, yyyy") : '-'}</DescriptionDetails>
                    <DescriptionTerm>Customer</DescriptionTerm>
                    <DescriptionDetails>{order.customer_fullname || '-'}</DescriptionDetails>
                    <DescriptionTerm>Status</DescriptionTerm>
                    <DescriptionDetails>{order.status}</DescriptionDetails>
                    <DescriptionTerm>Warehouse</DescriptionTerm>
                    <DescriptionDetails>{order.warehouse_name || '-'}</DescriptionDetails>
                    <DescriptionTerm>Remark</DescriptionTerm>
                    <DescriptionDetails>{order.remark || '-'}</DescriptionDetails>
                    <DescriptionTerm>SQL Account Update Count</DescriptionTerm>
                    <DescriptionDetails>
                        {order.sql_account_updatecount ?? '-'}
                        {order.sql_account_qr_synced_at && (
                            <span className="ml-2 text-xs text-zinc-500">
                                (last synced {dateFormat(order.sql_account_qr_synced_at, "mmmm dd, yyyy h:MM TT")})
                            </span>
                        )}
                    </DescriptionDetails>
                </DescriptionList>
            </Card>

            <Card className="mt-12 p-6">
                <Subheading>Items</Subheading>
                {order.items && order.items.length > 0 ? (
                    <Table className="mt-4 [--gutter:theme(spacing.6)] lg:[--gutter:theme(spacing.10)]">
                        <TableHead>
                            <TableRow>
                                <TableHeader>Description</TableHeader>
                                <TableHeader>Quantity</TableHeader>
                                <TableHeader>UOM</TableHeader>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {order.items.map((item, idx) => (
                                <TableRow key={idx} title={`${idx}`}>
                                    <TableCell>{item.is_other ? item.description : (item.product_description || item.item_no || '-')}</TableCell>
                                    <TableCell className="text-zinc-500">{item.quantity}</TableCell>
                                    <TableCell className="text-zinc-500">{item.uom || '-'}</TableCell>
                                </TableRow>
                            ))}
                        </TableBody>
                    </Table>
                ) : (
                    <p className="py-4 text-muted-foreground text-sm">No items.</p>
                )}
            </Card>

            <Card className="mt-12 p-6">
                <Subheading>QR Codes</Subheading>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-8 mt-4">
                    <div>
                        <p className="text-sm font-medium text-foreground mb-2">Scan for Delivery Man</p>
                        {dmQrImage && <img src={dmQrImage} alt="Delivery Man QR" className="w-24 h-24" />}
                    </div>
                    {order.sub_delivery_man_token && (
                        <div>
                            <p className="text-sm font-medium text-foreground mb-2">Scan for Sub-Delivery (Second Delivery Man)</p>
                            {subDmQrImage && <img src={subDmQrImage} alt="Sub-Delivery Man QR" className="w-24 h-24" />}
                        </div>
                    )}
                    <div>
                        <p className="text-sm font-medium text-foreground mb-2">Scan for Customer</p>
                        {custQrImage && <img src={custQrImage} alt="Customer QR" className="w-24 h-24" />}
                    </div>
                </div>
            </Card>

            <Card className="mt-12 p-6">
                <Subheading>Delivery Man Confirmation</Subheading>
                {deliveryMan ? (
                    <DescriptionList className="mt-4">
                        <DescriptionTerm>Driver Name</DescriptionTerm>
                        <DescriptionDetails>{deliveryMan.driver_name || '-'}</DescriptionDetails>
                        <DescriptionTerm>Vehicle No</DescriptionTerm>
                        <DescriptionDetails>{deliveryMan.vehicle_no || '-'}</DescriptionDetails>
                        <DescriptionTerm>Notes</DescriptionTerm>
                        <DescriptionDetails>{deliveryMan.notes || '-'}</DescriptionDetails>
                        <DescriptionTerm>Confirmed Time</DescriptionTerm>
                        <DescriptionDetails>{deliveryMan.confirmed_time ? dateFormat(deliveryMan.confirmed_time, "mmmm dd, yyyy hh:MM:ss") : '-'}</DescriptionDetails>
                        <DescriptionTerm>Proven Image</DescriptionTerm>
                        <DescriptionDetails>
                            {dmImages?.proven_image ? (
                                <img className="w-1/2 max-w-xs rounded-lg shadow mt-2" src={`${process.env.REACT_APP_SERVER_URL}${dmImages.proven_image}`} alt="Delivery proof" />
                            ) : <p>No image</p>}
                        </DescriptionDetails>
                    </DescriptionList>
                ) : (
                    <p className="py-8 text-muted-foreground text-sm">Not confirmed yet.</p>
                )}
            </Card>

            {order.sub_delivery_man_token && (
                <Card className="mt-12 p-6">
                    <Subheading>Sub-Delivery Man Confirmation</Subheading>
                    {subDeliveryMan ? (
                        <DescriptionList className="mt-4">
                            <DescriptionTerm>Driver Name</DescriptionTerm>
                            <DescriptionDetails>{subDeliveryMan.driver_name || '-'}</DescriptionDetails>
                            <DescriptionTerm>Vehicle No</DescriptionTerm>
                            <DescriptionDetails>{subDeliveryMan.vehicle_no || '-'}</DescriptionDetails>
                            <DescriptionTerm>Notes</DescriptionTerm>
                            <DescriptionDetails>{subDeliveryMan.notes || '-'}</DescriptionDetails>
                            <DescriptionTerm>Confirmed Time</DescriptionTerm>
                            <DescriptionDetails>{subDeliveryMan.confirmed_time ? dateFormat(subDeliveryMan.confirmed_time, "mmmm dd, yyyy hh:MM:ss") : '-'}</DescriptionDetails>
                            <DescriptionTerm>Proven Image</DescriptionTerm>
                            <DescriptionDetails>
                                {subDmImages?.proven_image ? (
                                    <img className="w-1/2 max-w-xs rounded-lg shadow mt-2" src={`${process.env.REACT_APP_SERVER_URL}${subDmImages.proven_image}`} alt="Sub-delivery proof" />
                                ) : <p>No image</p>}
                            </DescriptionDetails>
                        </DescriptionList>
                    ) : (
                        <p className="py-8 text-muted-foreground text-sm">Not confirmed yet.</p>
                    )}
                </Card>
            )}

            <Card className="mt-12 p-6">
                <Subheading>Customer Confirmation</Subheading>
                {customerConfirm ? (
                    <DescriptionList className="mt-4">
                        <DescriptionTerm>Recipient Name</DescriptionTerm>
                        <DescriptionDetails>{customerConfirm.recipient_name || '-'}</DescriptionDetails>
                        <DescriptionTerm>Notes</DescriptionTerm>
                        <DescriptionDetails>{customerConfirm.notes || '-'}</DescriptionDetails>
                        <DescriptionTerm>Confirmed Time</DescriptionTerm>
                        <DescriptionDetails>{customerConfirm.confirmed_time ? dateFormat(customerConfirm.confirmed_time, "mmmm dd, yyyy hh:MM:ss") : '-'}</DescriptionDetails>
                        <DescriptionTerm>Proven Image</DescriptionTerm>
                        <DescriptionDetails>
                            {custImages?.proven_image ? (
                                <img className="w-1/2 max-w-xs rounded-lg shadow mt-2" src={`${process.env.REACT_APP_SERVER_URL}${custImages.proven_image}`} alt="Receipt proof" />
                            ) : <p>No image</p>}
                        </DescriptionDetails>
                        <DescriptionTerm>Signature</DescriptionTerm>
                        <DescriptionDetails>
                            {custImages?.signature_image ? (
                                <img className="w-1/2 max-w-xs rounded-lg shadow mt-2 border border-gray-200" src={`${process.env.REACT_APP_SERVER_URL}${custImages.signature_image}`} alt="Signature" />
                            ) : <p>No signature</p>}
                        </DescriptionDetails>
                    </DescriptionList>
                ) : (
                    <p className="py-8 text-muted-foreground text-sm">Not confirmed yet.</p>
                )}
            </Card>
        </>
    );
};

export default ViewTrackingOrderPage;
